import slugify from 'slugify';
import type { OpeningHours, IPeriod } from 'App/Types/GooglePlaces';
import ResponsiveImage from '../Models/Statamic/Components/ResponsiveImage';
// import ResponsiveImage from 'App/Models/Statamic/Components/ResponsiveImage';

/**
 * Helper to use slugify lib with some default options
 */
export const makeSlug = (str: string) => {
  // Pre-set the defaults for https://www.npmjs.com/package/slugify
  return slugify(str, {
    replacement: '-',
    remove: /[*+~.()'"!:@]/g,
    lower: true,
  });
};

/**
 * Convert 24 hour time to 12 hour time
 * @param time String of 24 hour time, i.e. '16:00'
 */
export function convertTo12HourTime(time: string) {
  let hours;
  let minutes;

  // Check if the input contains a colon
  if (time.includes(':')) {
    [hours, minutes] = time.split(':').map(Number);
  } else {
    hours = Number(time);
    minutes = 0; // Default to 0 minutes if not provided
  }

  const amOrPm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12; // Convert 0 to 12 for midnight, and 13-23 to 1-11 for afternoon/evening
  return `${hours}:${minutes.toString().padStart(2, '0')} ${amOrPm}`;
}

/** Array of day short name */
const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Get the hours the store is open today, given the opening hours
 * @param hours The Google Places regularOpeningHours, from the Contentful GlobalSettings object
 * @param convert Whether or not to convert it to 12 hour time
 */
export const hoursToday = (hours: OpeningHours, convert = false) => {
  const date = new Date();
  const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

  const todayPeriods = hours.periods.filter((period) => period.open?.day === dayOfWeek);

  if (todayPeriods.length === 0) {
    return null; // Closed today
  }

  const today = todayPeriods.map((period) => {
    const startHour = String(period.open?.hour ?? 0).padStart(2, '0');
    const startMinute = String(period.open?.minute ?? 0).padStart(2, '0');
    const start = `${startHour}:${startMinute}`;
    // Forcing it to have a "close" value, since it will
    const endHour = String(period.close!.hour).padStart(2, '0');
    const endMinute = String(period.close!.minute).padStart(2, '0');
    const end = `${endHour}:${endMinute}`;
    return { start, end };
  });

  if (convert) {
    today.forEach((time) => {
      time.start = convertTo12HourTime(time.start);
      if (time.end) {
        time.end = convertTo12HourTime(time.end);
      }
    });
  }

  // Millbank only has one period per day, so we're assuming that here
  return today[0];
};

/**
 * Check if the store is open now, given the opening hours
 * @param hours The Google Places regularOpeningHours, from the Contentful GlobalSettings object
 */
export function isOpenNow(hours: OpeningHours) {
  const date = new Date();
  const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

  const todayPeriods = hours.periods.filter((period) => period.open?.day === dayOfWeek);

  if (todayPeriods.length === 0) {
    return false; // Closed today
  }

  // Current time in minutes since midnight
  const currentMinutesSinceMidnight = date.getHours() * 60 + date.getMinutes();

  for (const period of todayPeriods) {
    if (!period.open?.hour) {
      continue; // Skip if no open time
    }

    const openTime = period.open.hour * 60 + (period.open.minute ?? 0);
    const closeTime = period.close?.hour
      ? period.close.hour * 60 + (period.close?.minute ?? 0)
      : null;

    const doesNotClose = closeTime === null;
    const isWithinRange = doesNotClose ||
      currentMinutesSinceMidnight >= openTime && currentMinutesSinceMidnight < closeTime;

    if (doesNotClose || isWithinRange) {
      return true; // Currently open
    }
  }

  return false; // Currently closed
};

/** Helper to compare time strings in "HH:mm" format */
const isTimeAfter = (time1: string, time2: string) => {
  return time1 > time2;
};

/**
 * Get a message about the next opening time for the store, given the opening hours
 * @param hours The Google Places regularOpeningHours, from the Contentful GlobalSettings object
 * @param convert Whether or not to convert it to 12 hour time
 */
export function getNextOpeningTime(hours: OpeningHours, convert = false): string {
  const currentTime = new Date();

  // Get the current day and time
  const currentDayIndex = currentTime.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const currentHours = currentTime.getHours();
  const currentMinutes = currentTime.getMinutes();
  const currentTimeString = `${String(currentHours).padStart(2, '0')}:${String(currentMinutes).padStart(2, '0')}`;

  // Loop through today and the following days to find the next opening time
  for (let i = 0; i < 7; i++) {
    const dayIndex = (currentDayIndex + i) % 7;
    const dayPeriods = hours.periods.filter((period) => period.open?.day === dayIndex);

    if (dayPeriods.length === 0) {
      continue; // Skip if the store is closed on this day
    }

    for (const period of dayPeriods) {
      const start = `${String(period.open?.hour ?? 0).padStart(2, '0')}:${String(period.open?.minute ?? 0).padStart(2, '0')}`;
      const end = `${String(period.close!.hour).padStart(2, '0')}:${String(period.close!.minute).padStart(2, '0')}`;

      // If it's today and the current time is before the opening time
      if (i === 0 && isTimeAfter(start, currentTimeString)) {
        return `Closed until today at ${convert ? convertTo12HourTime(start) : start}`;
      }

      // If it's today and we're already past closing time
      if (i === 0 && end && isTimeAfter(currentTimeString, end)) {
        continue; // Skip today if it's already past closing time
      }

      // For future days
      if (i > 0) {
        return `Closed until ${DAYS_OF_WEEK[dayIndex]} at ${convert ? convertTo12HourTime(start) : start}`;
      }
    }
  }

  return 'The store is closed for the week';
}

/**
 * Takes Google Places API opening hours and condenses it so days with the same hours are put together
 * @param hours The Google Places regularOpeningHours, from the Contentful GlobalSettings object
 * @param convert Whether or not to convert it to 12 hour time
 * e.g. [
 * { days: "Sun", hours: "Closed" },
 * { days: "Mon - Fri", hours: "07:00 - 18:00" },
 * { days: "Sat", hours: "08:00 - 15:00" },
 * ]
 */
export function consolidateStoreHours(hours: OpeningHours, convert = false): { days: string; hours: string }[] {
  const consolidatedHours: { days: string; hours: string }[] = [];
  let currentGroup: { days: string[]; hours: string } | null = null;

  const formatPeriod = (period: IPeriod) => {
    const start = `${String(period.open?.hour ?? 0).padStart(2, '0')}:${String(period.open?.minute ?? 0).padStart(2, '0')}`;
    const end = `${String(period.close!.hour).padStart(2, '0')}:${String(period.close!.minute).padStart(2, '0')}`;
    return `${convert ? convertTo12HourTime(start) : start} - ${convert ? convertTo12HourTime(end) : end}`;
  };

  for (let i = 0; i < 7; i++) {
    const dayPeriods = hours.periods.filter((period) => period.open?.day === i);
    const dayHours = dayPeriods.length === 0 ? 'Closed' : formatPeriod(dayPeriods[0]);

    if (currentGroup && currentGroup.hours === dayHours) {
      currentGroup.days.push(DAYS_OF_WEEK[i]);
    } else {
      if (currentGroup) {
        consolidatedHours.push({
          days: currentGroup.days.length > 1 ? `${currentGroup.days[0]} - ${currentGroup.days[currentGroup.days.length - 1]}` : currentGroup.days[0],
          hours: currentGroup.hours,
        });
      }
      currentGroup = { days: [DAYS_OF_WEEK[i]], hours: dayHours };
    }
  }

  if (currentGroup) {
    consolidatedHours.push({
      days: currentGroup.days.length > 1 ? `${currentGroup.days[0]} - ${currentGroup.days[currentGroup.days.length - 1]}` : currentGroup.days[0],
      hours: currentGroup.hours,
    });
  }

  // Reorder so Monday is first and Sunday is last
  const order = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const sortIndex = (days: string) => order.indexOf(days.split(' ')[0]);
  consolidatedHours.sort((a, b) => sortIndex(a.days) - sortIndex(b.days));

  return consolidatedHours;
}

/**
 * Convert Google Places API's OpeningHours to schema.org openingHoursSpecification format
 * @param hours The Google Places regularOpeningHours, from the Contentful GlobalSettings object
 */
export function convertHoursToSchema(hours: OpeningHours) {
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const schemaHours = hours.periods.map((period) => {
    const day = daysOfWeek[period.open?.day ?? 0];
    const opens = `${String(period.open?.hour ?? 0).padStart(2, '0')}:${String(period.open?.minute ?? 0).padStart(2, '0')}`;
    const closes = `${String(period.close?.hour ?? 0).padStart(2, '0')}:${String(period.close?.minute ?? 0).padStart(2, '0')}`;

    return {
      '@type': 'OpeningHoursSpecification',
      'dayOfWeek': day,
      'opens': opens,
      'closes': closes,
    };
  });

  // TODO: Loop through this in order and combine days with the same hours
  // e.g. "dayOfWeek" should be an array.
  // I think it's fine as-is, but it could be less verbose.

  return schemaHours;
}

/**
 * Serialize a value to JSON for embedding inside an inline `<script type="application/ld+json">`
 * tag. `JSON.stringify` does NOT escape `<`, so a `</script>` sequence in the data (e.g. HTML
 * from a CMS accordion answer or a product description) would terminate the script tag early and
 * allow markup injection. We escape the HTML-significant characters as their `\uXXXX` forms, which
 * are equivalent inside a JSON string but can't break out of the tag. Also escapes the U+2028 /
 * U+2029 line separators, which are valid in JSON but illegal in JS string literals.
 *
 * Use with Edge's `safe()` so the result isn't double-escaped: `{{ safe(jsonForScript(x)) }}`.
 * Applies to any JSON embedded in an inline `<script>` — JSON-LD blocks, `window.x = {{ ... }}`
 * data hand-offs, analytics payloads, etc.
 */
export function jsonForScript(value: unknown): string {
  // JSON.stringify returns undefined for undefined / functions / symbols; emit `null` so
  // callers always get a valid JSON literal rather than a runtime error on .replace().
  const json = JSON.stringify(value);
  if (json === undefined) {
    return 'null';
  }

  return json
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

/** Format a price in a given currency with or without decimal places */
export const formatPrice = (price: number, currency: string, decimals: boolean) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    // Remove decimals
    minimumFractionDigits: decimals ? 2 : 0,
    maximumFractionDigits: decimals ? 2 : 0,
  });

  const formattedPrice = formatter.format(price);

  // It will add a currency symbol to be more explicit based on the machine's locale, which we don't want.
  // https://github.com/nodejs/node/issues/15265#issuecomment-776942859
  const priceWithoutCurrency = formattedPrice.replace(/[A-Z]+/, '');

  return priceWithoutCurrency;
};

/**
 * Generate a table for a specifications category
 * @param specs Specifications from a product API result
 * @returns HTML for a table of the specifications
 */
export const generateSpecsTable = (specs: any) => {
  return `<table class="pdp__table">
      <thead>
        <tr class="pdp__table-row pdp__table-row--heading">
          <th>${specs.categoryName}</th>
        </tr>
      </thead>
      <tbody>
        ${specs.entries
          // eslint-disable-next-line @typescript-eslint/indent
          .map((spec) => `
            <tr class="pdp__table-row">
              <td>${spec.name}</td>
              <td>${spec.value}</td>
            </tr>
          `).join('')}
      </tbody>
    </table>`;
};

/**
 * Model contentful items, stripping out any draft items
 * @param data The contentful field. Either an array or single object
 * @param Model The model to use to create the object
 */
export const modelWithoutDrafts = (
  data: Record<string, any> | Record<string, any>[],
  Model: any,
) => {
  // If the data is an array, map over it and create a new object
  if (Array.isArray(data)) {
    return data.map((item: Record<string, any>) => {
      if (!item.fields) {
        return {};
      }
      return new Model(item);
    }).filter((obj) => Object.keys(obj).length);
  }

  // If the data is not an array, create single object if has fields
  if (data.fields) {
    return new Model(data);
  }

  // Otherwise, return empty object
  return {};
};

/**
 * Takes a string and if the last word is less than 7 characters, adds a &nbsp; to prevent widowed characters
 * @param string The string
 */
export const preventWidow = (text: string): string => {
  return text.replace(/\s([^\s]{1,6})$/, '&nbsp;$1');
};

export function createResponsiveImage(data: any): ResponsiveImage | null {
  if (!data?.image && !data?.mobile_image) {
    return null;
  }

  try {
    return new ResponsiveImage(data);
  } catch (error) {
    return null;
  }
}

/**
 * Options for transforming images
 *
 * https://developers.cloudflare.com/images/transform-images/transform-via-url/#options
 */
type ImageTransformOptions = {
  width?: number;
  height?: number;
  quality?: number;
  fit?: 'scale-down' | 'cover' | 'contain';
  format?: 'webp' | 'avif' | 'jpeg' | 'png';
  /**
   * Either "auto", a side ("left", "right", "top", "bottom"), or coordinates
   * specified on a scale from 0.0 (top or left) to 1.0 (bottom or right), 0.5 being the center.
   *
   * The X and Y coordinates are separated by lowercase x in the URL format.
   * For example, 0x1 means left and bottom, 0.5x0.5 is the center, 0.5x0.33 is a point in the top third of the image.
   *
   * The "auto" option selects a focal point based on saliency detection (using maximum symmetric surround algorithm).
   */
  gravity?: string;
  // Should I add an "any" field for additional options?
  // [key: string]: string | number | undefined;
};

/**
 * Generate an image transformation URL via Cloudflare's CDN
 * This function will transform the image URL to use Cloudflare's CDN for image transformations.
 * It will add the necessary parameters to the URL to apply the transformations.
 * If the URL already contains the transformation parameters, it will return the URL as-is.
 * If the URL is for an SVG, it will return the original URL without any transformations.
 * @param url The original image URL.
 * @param options The transformation options.
 * @returns The transformed image URL.
 */
export function generateTransformedImageUrl(
  url: string | null | undefined,
  options: ImageTransformOptions,
  domain = 'https://stage.millbankhardware.com',
) {
  // The dev asset server's host and port come from --encore-args in package.json, so match
  // whichever local address it was started on rather than one spelling of it. The lookahead
  // ends the host at the port so `http://localhost:8081evil.com` can't rewrite to a different
  // host, and leaves the path in place for the replacement.
  const DEV_HOST_REGEX = /^https?:\/\/(127\.0\.0\.1|0\.0\.0\.0|localhost|\[::1\]):\d+(?=[/?#]|$)/;

  // Media and ResponsiveImage swallow their own constructor errors, so a missing or malformed
  // CMS asset reaches us as undefined rather than failing at the source. Bail rather than
  // dereference: lazysizes skips a falsy data-src/data-srcset, so the image is simply absent.
  if (!url) {
    return '';
  }

  // If the URL is already transformed or is an SVG, return it as-is
  if (url.endsWith('.svg') || url.includes('cdn.millbankhardware.com/image/')) {
    return url;
  } else if (url.startsWith('/')) {
    // If the URL is a relative path, prepend the domain
    url = `${domain}${url}`;
  } else if (DEV_HOST_REGEX.test(url)) {
    // If on localhost dev, replace the URL with the configured domain
    url = url.replace(DEV_HOST_REGEX, domain);
  }

  // set default quality
  if (!options.quality) {
    options.quality = 45;
  }

  const optionsString = Object.entries(options)
    .map(([key, value]) => `${key}=${value}`)
    .join(',');

  // Otherwise, transform the URL using the CDN
  return `https://cdn.millbankhardware.com/image/${optionsString}/${url}`;
};

/** URL of the product image placeholder */
export const PLACEHOLDER_IMAGE_URL = '/static/img/product-image_placeholder.jpg';

/** URL of the small product image placeholder */
export const SMALL_PLACEHOLDER_IMAGE_URL = '/static/img/product-image_placeholder_small.jpg';

export const formatDate = (dateInput: string, includeTime = false): string => {
  if (!dateInput) {
    return '';
  }

  const date = new Date(dateInput);

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  if (includeTime) {
    options.hour = 'numeric';
    options.minute = 'numeric';
  }

  return date.toLocaleDateString('en-US', options);
};

/** Format a number as a currency string in CAD. */
export const formatMoney = (value: string | number): string => {
  const number = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(number)) {
    return '$0.00';
  };

  const formatted = number.toLocaleString('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Remove currency code prefix (e.g. "CA" in "CA$1,234.56") but preserve thousands-separator commas
  return formatted.replace(/^[A-Z]+\s?/, '');
};

/** Format a number with thousands separators and exactly 2 decimal places, no currency symbol. */
export const formatPriceNumber = (n: number): string =>
  n.toLocaleString('en-CA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Take pagination returned from Millbank API and prep it for templating
type Pagination = {
  perPage: number
  currentPage: number
  firstPage: number
  lastPage: number
  total: number
  hasPages: boolean
  hasMorePages: boolean
  hasTotal: boolean
  isEmpty: boolean
};

export const getPaginationRange = (pagination: Pagination): number[] => {
  const { currentPage, lastPage } = pagination;

  let start = Math.max(currentPage - 2, 1);
  let end = Math.min(start + 4, lastPage);

  if (end - start < 4) {
    start = Math.max(end - 4, 1);
  }

  const pages: number[] = [];

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  return pages;
};

/**
 * Cookiebot sets the CookieConsent cookie as a javascript object single quotes or none,
 * which is invalid JSON, so we need to fix it ourselves.
 * https://stackoverflow.com/questions/79309490/integrating-usercentrics-cookiebot-with-angular-18-and-ssr
 * @param cookie The cookie string to parse
 * @returns The parsed cookie consent object or null if not found
 */
export const parseCookiebotCookie = (cookie: string) => {
  if (!cookie) {
    return null;
  }

  const cleaned = cookie
    .replace(/'/g, '"') // Replace single quotes with double quotes
    .replace(/([a-zA-Z0-9_]+):/g, '"$1":'); // Add double quotes around property names
  const cookieConsentObj = JSON.parse(cleaned);
  return cookieConsentObj;
};

// Country and Province/Territory normalization helpers

// Minimal country maps (extend as needed)
const COUNTRY_BY_CODE: Record<string, string> = {
  CA: 'Canada',
  US: 'United States of America',
  GB: 'United Kingdom',
  AU: 'Australia',
  NZ: 'New Zealand',
};

const COUNTRY_CODE_BY_NAME: Record<string, string> = Object.fromEntries(
  Object.entries(COUNTRY_BY_CODE).map(([code, name]) => [name.toLowerCase(), code])
);

export function countryInfoFrom(input?: string | null): { code: string; name: string } {
  if (!input) {
    return { code: '', name: '' };
  }
  const raw = input.trim();
  const up = raw.toUpperCase();
  const low = raw.toLowerCase();
  if (up.length === 2 && COUNTRY_BY_CODE[up]) {
    return { code: up, name: COUNTRY_BY_CODE[up] };
  }
  const code = COUNTRY_CODE_BY_NAME[low];
  if (code) {
    return { code, name: COUNTRY_BY_CODE[code] };
  }
  // Fallbacks
  if (up.length === 2) {
    return { code: up, name: toTitleCase(raw) };
  }
  return { code: up.slice(0, 2), name: toTitleCase(raw) };
}

function toTitleCase(s: string): string {
  if (!s || typeof s !== 'string') {
    return s;
  }
  return s.replace(/\w\S*/g, (t) => t[0].toUpperCase() + t.slice(1).toLowerCase());
}

// Canadian provinces/territories
// - order matters for reverse object below
// - official, uppercase of each must be last
const CA_PROVINCE_CODE_BY_NAME: Record<string, string> = {
  'ab': 'AB',
  'alberta': 'AB',
  'Alberta': 'AB',
  'bc': 'BC',
  'british columbia': 'BC',
  'British Columbia': 'BC',
  'mb': 'MB',
  'manitoba': 'MB',
  'Manitoba': 'MB',
  'nb': 'NB',
  'new brunswick': 'NB',
  'New Brunswick': 'NB',
  'nl': 'NL',
  'newfoundland/labrador': 'NL',
  'Newfoundland/Labrador': 'NL', // bistrack's version
  'newfoundland / labrador': 'NL',
  'Newfoundland / Labrador': 'NL',
  'newfoundland & labrador': 'NL',
  'Newfoundland & Labrador': 'NL', // potential alternative
  'newfoundland and labrador': 'NL',
  'Newfoundland and Labrador': 'NL', // last as the official name
  'nt': 'NT',
  'northwest territories': 'NT',
  'Northwest Territories': 'NT',
  'ns': 'NS',
  'nova scotia': 'NS',
  'Nova Scotia': 'NS',
  'nu': 'NU',
  'nunavut': 'NU',
  'Nunavut': 'NU',
  'on': 'ON',
  'ontario': 'ON',
  'Ontario': 'ON',
  'pe': 'PE',
  'pei': 'PE',
  'prince edward island': 'PE',
  'Prince Edward Island': 'PE',
  'qc': 'QC',
  'quebec': 'QC',
  'Quebec': 'QC',
  'sk': 'SK',
  'saskatchewan': 'SK',
  'Saskatchewan': 'SK',
  'yt': 'YT',
  'yukon territories': 'YT',
  'Yukon Territories': 'YT', // bistrack's version
  'yukon': 'YT',
  'Yukon': 'YT', // last as the official name
};

const CA_PROVINCE_BY_CODE: Record<string, string> = Object.fromEntries(
  Object.entries(CA_PROVINCE_CODE_BY_NAME).map(([name, code]) => [code.toUpperCase(), name])
);

export function provinceCodeFrom(input?: string | null): string {
  if (!input) {
    return '';
  }
  const key = input.trim().toLowerCase();
  return CA_PROVINCE_CODE_BY_NAME[key] || input.toUpperCase();
}

export function provinceInfoFrom(input?: string | null): { code: string; name: string } {
  if (!input) {
    return { code: '', name: '' };
  }
  const raw = input.trim();
  const up = raw.toUpperCase();
  const low = raw.toLowerCase();
  if (up.length === 2 && CA_PROVINCE_BY_CODE[up]) {
    return { code: up, name: CA_PROVINCE_BY_CODE[up] };
  }
  const code = CA_PROVINCE_CODE_BY_NAME[low];
  if (code) {
    return { code, name: CA_PROVINCE_BY_CODE[code] };
  }
  // Fallbacks
  if (up.length === 2) {
    return { code: up, name: toTitleCase(raw) };
  }
  return { code: up.slice(0, 2), name: toTitleCase(raw) };
}
